/**
Author - Brijesh Pandey
Git - https://github.com/Brijesh-Pandey
**/

import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactAudioPlayer from 'react-audio-player';

import {ActionButton, ActionButtonSecondary, Toggler} from 'screens/commons';

import {KycScreenMapping, KycFlowMapping} from 'types';
import IMAGES from 'constants/Images';
import useMSClarity from 'hooks/useMSClarity';

const englishAudio = 'https://i.wlycdn.com/wealthy-kyc/digilocker-english-raveena-audio.mp3';
const hindiAudio = 'https://i.wlycdn.com/wealthy-kyc/digilocker-hindi-audio.mp3';

const possibleLanguages = [
  {
    id: 0,
    langauge: 'english',
    source: englishAudio,
    displayText: 'हिंदी',
  },
  {
    id: 1,
    langauge: 'hindi',
    source: hindiAudio,
    displayText: 'English',
  },
];

const propTypes = {
  handleDigioInitialization: PropTypes.func.isRequired,
  kycData: PropTypes.object.isRequired,
};

const ClientKyc = ({handleDigioInitialization, handleGoBack, kycData}) => {
  const [flipAudio, setFlipAudio] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState(possibleLanguages[0]);

  const {sendClarityEvent} = useMSClarity();

  useEffect(() => {
    if (activeLanguage.id === 1) {
      setActiveLanguage(possibleLanguages[0]);
    } else {
      setActiveLanguage(possibleLanguages[1]);
    }
  }, [flipAudio]);

  const {stage, flow} = kycData;
  const stageText = stage.split('_').join(' ');
  const showDetailedDescription = !!(
    stage === KycScreenMapping.DIGILOCKER || stage === KycScreenMapping.E_SIGN
  );

  let title = `Next step is ${stageText} KYC`;
  let subTitle = "Thank you for your patience, please click on the 'NEXT' button to move forward";
  let description = '';
  let imgPath = '';
  let actionButtonText = 'Continue';
  let goBackButtonText = 'Process Manually';
  let classname = '';
  let supportingImage = '';
  let instructionText = '';

  switch (stage) {
    case KycScreenMapping.DIGILOCKER:
      title = '';
      subTitle = 'Connect with DigiLocker';
      description =
        'DigiLocker allows secure access to your government-issued documents. Your data remains private and is only used for verification.';
      imgPath = IMAGES.digiLocker;
      classname = 'digilocker';
      instructionText = 'Follow these steps on the next screen:';
      break;
    case KycScreenMapping.SELFIE:
      title = '';
      subTitle = 'For Identity Verification';
      description = '';
      actionButtonText = 'Capture Selfie';
      imgPath = IMAGES.selfieUpload;
      supportingImage = IMAGES.selfieGuidelines;
      classname = 'selfie';
      instructionText =
        'Please ensure you’re in a well-lit area and your face is clearly visible. This helps us confirm your identity securely.';
      break;
    case KycScreenMapping.SPECIMEN_SIGN:
      title = 'Specimen signature as per your PAN';
      subTitle = 'Required for Account Activation';
      description = '';
      actionButtonText = 'Continue to Signature';
      imgPath = IMAGES.specimenSign;
      classname = 'specimen-sign';
      instructionText = '';
      break;
    case KycScreenMapping.E_SIGN:
      title = '';
      subTitle = 'For Digital Document Authorization';
      description =
        'Your e-signature will be used to authenticate documents electronically. This has the same legal validity as a physical signature.';
      actionButtonText = 'Continue to E-SIGN';
      imgPath = IMAGES.aadhaar_number_image;
      classname = 'e-sign';
      instructionText = 'Get your Aadhaar number ready for E-Sign';
      break;
    case KycScreenMapping.DDPI:
      title = '';
      subTitle = 'Required for Trading Activities';
      description =
        'DDPI allows seamless movement of securities for trading. Your authorization is required to proceed with account setup';
      actionButtonText = 'CONTINUE TO DDPI';
      imgPath = IMAGES.mandateImg;
      classname = 'ddpi';
      instructionText = '';
      break;
    default:
      title = `Next step is ${stageText} KYC`;
      subTitle = 'Please complete this step to continue with your application';
      description = '';
      imgPath = '';
      instructionText = '';
      break;
  }

  return (
    <KycWrapper>
      <ContentSection className={`${classname}`}>
        {stage === KycScreenMapping.DIGILOCKER ? (
          <DigilockerInstructionWrapper>
            {instructionText ? <InstructionText>{instructionText}</InstructionText> : null}
            <img
              alt="DigiLocker instructions"
              className="digilocker-instruction"
              src={IMAGES.digilocker_instruction}
            />
          </DigilockerInstructionWrapper>
        ) : (
          <>
            <KycImage className={`${classname}`}>
              {title ? <Title className="page-main-heading">{title}</Title> : null}
              <img alt={title} className="kyc-image" src={imgPath} />
              {supportingImage ? (
                <img className="guidelines-image" alt="guidelines" src={supportingImage} />
              ) : null}
            </KycImage>
            {instructionText ? (
              <AlertBanner className={`alert-banner-${classname}`}>
                <InfoImage src={IMAGES.infoIconImage} alt="alert" />
                <AlertText
                  className="body-small"
                  dangerouslySetInnerHTML={{
                    __html: instructionText,
                  }}
                />
              </AlertBanner>
            ) : null}
          </>
        )}

        {stage === KycScreenMapping.DIGILOCKER ? (
          <HelpContainer>
            <ReactAudioPlayer src={activeLanguage.source} autoPlay controls />
            <Toggler values={['हिंदी', 'English']} switchToggle={setFlipAudio} toggle={flipAudio} />
          </HelpContainer>
        ) : null}

        {showDetailedDescription ? (
          <NoteContainer>
            {stage !== KycScreenMapping.SPECIMEN_SIGN ? (
              <>
                <PointWrapper>
                  <BulletImage
                    alt="bullet-image"
                    src="https://i.wlycdn.com/wealthy-home-page/bullet-star.svg"
                    loading="lazy"
                  />
                  <NoteDescription>
                    Keep your Aadhaar number and Aadhaar-linked mobile number ready for
                    verification.
                  </NoteDescription>
                </PointWrapper>

                <PointWrapper>
                  <BulletImage
                    alt="bullet-image"
                    src="https://i.wlycdn.com/wealthy-home-page/bullet-star.svg"
                    loading="lazy"
                  />
                  <NoteDescription>
                    A one-time password (OTP) will be sent to your Aadhaar-linked mobile number for
                    authentication.
                  </NoteDescription>
                </PointWrapper>
              </>
            ) : null}
            {stage === KycScreenMapping.DIGILOCKER ? (
              <>
                <PointWrapper>
                  <BulletImage
                    alt="bullet-image"
                    src="https://i.wlycdn.com/wealthy-home-page/bullet-star.svg"
                    loading="lazy"
                  />
                  <NoteDescription>
                    If you need to reset your DigiLocker PIN, select "Forgot security PIN" option
                    during the verification process.
                  </NoteDescription>
                </PointWrapper>
              </>
            ) : null}
          </NoteContainer>
        ) : description ? (
          <Description>{description}</Description>
        ) : null}

        {stage === KycScreenMapping.DIGILOCKER && flow === KycFlowMapping.MF_NRI ? (
          <SuggestionBox>
            <img
              src={IMAGES.infoIconImage}
              alt="info-icon"
              title="Choose Manual option after clicking this button"
            />
            <span className="body-small text-tertiary">
              If you experience issues with DigiLocker, you can proceed with the manual option
              option.
            </span>
          </SuggestionBox>
        ) : null}

        <FooterSection>
          <ActionButton
            buttonText={actionButtonText}
            callback={() => {
              handleDigioInitialization();
              sendClarityEvent(`Init_DIGIO_SDK_${stage}_${kycData.id}`);
            }}
            className="next-button"
          />

          {/* Only show this button for MF_NRI flow and on digilocker stage */}
          {stage === KycScreenMapping.DIGILOCKER && flow === KycFlowMapping.MF_NRI ? (
            <ActionButtonSecondary
              buttonText={goBackButtonText}
              callback={() => handleGoBack(flow)}
            />
          ) : null}
        </FooterSection>
      </ContentSection>
    </KycWrapper>
  );
};

const KycWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: auto;

  .selfie,
  .specimen-sign,
  .e-sign {
    justify-content: start;
  }
  .e-sign {
    padding-top: 2rem;
  }
  .alert-banner-specimen-sign {
    margin: 1.5rem 0 0 0;
  }

  .alert-banner-e-sign {
    margin: 1rem 0 0.5rem 0;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  padding: ${(props) => props.theme.spacing.md};

  @media screen and (max-width: 768px) {
    padding: ${(props) => props.theme.spacing.md};
  }

  .kyc-image {
    height: 10rem;
    width: 10rem;
    object-fit: contain;
  }

  &.digilocker {
    .digilocker-instruction-wrapper {
      width: 100%;
      max-width: 600px;
      margin: ${(props) => props.theme.spacing.sm} 0;
      display: flex;
      flex-direction: column;
      gap: ${(props) => props.theme.spacing.sm};
    }

    .digilocker-instruction {
      width: 100%;
      max-height: 24rem;
      object-fit: contain;
    }
  }

  .specimen-sign {
    .kyc-image {
      width: 44rem;
      height: unset;
      @media screen and (max-width: 768px) {
        padding-top: 2rem;
      }
    }
  }

  .selfie {
    .kyc-image {
      width: 32rem;
      height: unset;

      @media screen and (max-width: 768px) {
        padding-top: 2rem;
        width: 25rem;
      }
    }
    .guidelines-image {
      width: 43rem;
      height: unset;
    }
  }

  .e-sign {
    .kyc-image {
      height: unset;
      width: 28rem;
    }
  }
`;

const DigilockerInstructionWrapper = styled.div.attrs({
  className: 'digilocker-instruction-wrapper',
})``;

const InstructionText = styled.p.attrs({
  className: 'body-medium',
})`
  margin: 0;
  text-align: left;
  color: ${(props) => props.theme.colors.text.primary};
`;

const FooterSection = styled.div`
  width: 100%;
  max-width: 500px;
  padding: ${(props) => props.theme.spacing.md};
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  @media screen and (max-width: 768px) {
    padding: ${(props) => props.theme.spacing.sm};
  }
  position: sticky;
  bottom: 0;
`;

export const SuggestionBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${(props) => props.theme.spacing.sm};
  background: ${({theme}) => theme.colors.background.secondary};
  border: 1.5px solid ${({theme}) => theme.colors.brand.tertiary};
  border-radius: ${({theme}) => theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  margin-top: ${(props) => props.theme.spacing.xs};
  color: ${({theme}) => theme.colors.text.secondary};
  width: 100%;
  box-sizing: border-box;
  max-width: 600px;

  img {
    height: 1.5em;
    width: 1.5em;
    margin-right: ${(props) => props.theme.spacing.xs};
    flex-shrink: 0;
    vertical-align: middle;
    display: inline-block;
  }
`;

const HelpContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${(props) => props.theme.spacing.sm} 0;
  width: 100%;
  max-width: 400px;
  align-items: center;

  @media screen and (max-width: 768px) {
    max-width: 100%;
    justify-content: center;
    align-items: center;
  }
`;

const PointWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const BulletImage = styled.img`
  margin-right: ${(props) => props.theme.spacing.md};
  height: 1.25rem;
  margin-top: 0.8rem;
`;

const NoteContainer = styled.div`
  background-color: ${(props) => props.theme.colors.background.secondary};
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.lg};
  width: 100%;
  max-width: 600px;
  margin: 0;
`;

const NoteDescription = styled.div.attrs({
  className: 'body-small',
})`
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0.5rem 0 0 0;
  text-align: justify;
`;

const KycImage = styled.div`
  margin: ${(props) => props.theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 10rem;
  display: flex;
  flex-direction: column;
`;

const Description = styled.span.attrs({
  className: 'body-small',
})`
  margin: ${(props) => props.theme.spacing.md} 0 0;
  padding: 0;
  color: ${(props) => props.theme.colors.text.secondary};
  width: 80%;
  text-align: center;
`;

const AlertBanner = styled.div`
  background: ${(props) => props.theme.colors.brand.secondary};
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0;
  margin-top: 0.5rem;
`;

const AlertText = styled.p`
  color: ${(props) => props.theme.colors.brand.primary || '#6b21a8'};
  margin: 0;
  mark {
    background: none;
    font-weight: ${(props) => props.theme.typography.fontWeight.semiBold} !important;
    color: ${(props) => props.theme.colors.brand.primary || '#6b21a8'};
  }
`;
const InfoImage = styled.img`
  height: 1.25rem;
  width: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const Title = styled.p`
  margin: 0;
  text-align: left;
  align-self: center;
  font-weight: 500;
  padding-left: 1.9rem;
`;

ClientKyc.propTypes = propTypes;

export default ClientKyc;
